import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import FormBody from 'components/FormBody/index';
import useAppNavigate from 'hooks/useAppNavigate';
import { AppRoutes } from 'routes/routes.enums';
import Input from 'components/Inputs/Input/index';
import { Button } from 'components/Buttons/Button/index';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import { getErrorMessageForRequiredFields, removeSpaces } from 'utils/stringUtils';
import { FieldName } from 'pages/PasswordProtection/passwordProtection.enums';
import { PASSWORD_VALIDATION } from 'pages/PasswordProtection/passwordProtection.constants';
import { saveCredentialsToStorage } from 'pages/PasswordProtection/passwordProtection.utils';
import { passwordProtectionApi } from 'api/PasswordProtectionApi';

import styles from './PasswordProtection.module.scss';

interface LocationState {
  from: string;
}

export const PasswordProtection = () => {
  const navigator = useAppNavigate();
  const locationState = useLocation().state as LocationState;
  const [isLoading, setIsLoading] = useState(false);

  const {
    formState: { errors, isSubmitted },
    setValue,
    setError,
    clearErrors,
    trigger,
    watch,
    handleSubmit,
    register,
  } = useForm({
    defaultValues: {
      [FieldName.UserName]: '',
      [FieldName.Password]: '',
    },
  });
  const watcher = watch();

  useEffect(() => {
    register(FieldName.UserName, {
      required: getErrorMessageForRequiredFields('UserName'),
    });
    register(FieldName.Password, {
      required: getErrorMessageForRequiredFields('Password'),
      validate: (value) => {
        return PASSWORD_VALIDATION.isValid(value) || PASSWORD_VALIDATION.message;
      },
    });
  }, [register]);

  const submit = handleSubmit(async () => {
    try {
      setIsLoading(true);

      const response = await passwordProtectionApi.check({
        userName: watcher[FieldName.UserName],
        password: watcher[FieldName.Password],
      });

      if (response.hasAccess) {
        saveCredentialsToStorage({
          userName: watcher[FieldName.UserName],
          password: watcher[FieldName.Password],
        });

        if (locationState && locationState.from) {
          navigator(locationState.from);
        } else {
          navigator(AppRoutes.Home);
        }
      } else {
        setError(FieldName.UserName, {
          type: 'manual',
          message: 'Invalid Credentials',
        });
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className={styles.mainContainer}>
      <FormBody>
        <Input
          className={styles.userNameInput}
          label={'User Name'}
          value={watcher[FieldName.UserName]}
          onChange={(event: any) => {
            clearErrors([FieldName.UserName]);
            setValue(FieldName.UserName, removeSpaces(event.target.value));

            if (isSubmitted) {
              trigger(FieldName.UserName);
            }
          }}
          errorMessage={errors[FieldName.UserName]?.message}
        />
        <Input
          inputClassName={styles.passwordInput}
          label={'Password'}
          value={watcher[FieldName.Password]}
          onChange={(event: any) => {
            clearErrors([FieldName.Password]);
            setValue(FieldName.Password, removeSpaces(event.target.value));

            if (isSubmitted) {
              trigger(FieldName.Password);
            }
          }}
          errorMessage={errors[FieldName.Password]?.message}
        />
        <Button className={styles.submit} onClick={submit} isLoading={isLoading}>
          Submit
        </Button>
      </FormBody>
    </div>
  );
};
